import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import {
  InstUISettingsProvider,
  IamValidationProvider,
  IamValidationGuard,
  Flex,
  View
} from '@smarterservices/smarter-ui';
import { debounce } from '@instructure/debounce';
import SplitView from '../Common/SplitView';
import Header from '../Header';
import {
  getStore,
  usePlayerStore,
  useAppStore,
  useLayoutStore,
} from '../../stores/StoreContext';
import SessionDetails from '../SessionDetails';
import DenyConfirmation from '../SessionDetails/Checkin/DenyConfirmation';
import VideoPlayer from '../Player';
import SessionLoaderOverlay from './SessionLoaderOverlay';
import DraggableAnnotationsModal from '../SessionDetails/Annotations/DraggableAnnotationsModal';
import { useDraggableModal } from '@ui/shared/components/Common/DraggableModal';
import { useHotkeys } from 'react-hotkeys-hook';
import CONSTANTS from '../../config/constants';
import { FullScreenProvider } from '../../contexts/FullScreenContext';
import './styles.css';

export function ReactSession({
  connectionBundle,
  apiUrl,
  roomId,
  spToken,
  sessionSid,
  examSid,
  installSid,
  identity,
  websocketUrl,
  enabledComponents,
  video,
  type,
  viewMode,
  ...props
}) {
  const EventStore = getStore('eventStore');
  const LayoutStore = getStore('layoutStore');
  const AppStore = getStore('appStore');
  const PlayerStore = getStore('playerStore');

  const initialized = useAppStore((state) => state.initialized);
  const iam = useAppStore((state) => state.iam);

  // Check for debug URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isDebugMode = urlParams.has('debug');

  if (isDebugMode) {
    console.log('Session IAM Details:', {
      type: typeof iam,
      isNull: iam === null,
      isUndefined: iam === undefined,
      hasStatement: iam?.hasOwnProperty('Statement'),
      statementIsArray: Array.isArray(iam?.Statement),
      statementLength: iam?.Statement?.length,
      keys: iam ? Object.keys(iam) : [],
      stringified: JSON.stringify(iam)
    });
  }

  const hasTracks = usePlayerStore((state) => state.hasTracks);
  const videoContainerSize = useLayoutStore(
    (state) => state.containers.video.size
  );
  const controlsContainerSize = useLayoutStore(
    (state) => state.containers.controls.size
  );

  const isFullScreen = useLayoutStore(
    (state) => state.isFullScreen
  );
  const isCollapsed = useLayoutStore(
    (state) => state?.detailsSplitView?.isCollapsed
  );

  // Draggable annotations modal state
  const annotationsModal = useDraggableModal({
    initialPosition: { x: 100, y: 100 },
    initialSize: { width: 900, height: 700 },
    initialOpen: false
  });

  const sessionRef = useRef(null);
  const videoContainerRef = useRef(null);
  const videoControlsRef = useRef(null);
  const sessionDetailsRef = useRef(null);

  const [videoHeight, setVideoHeight] = useState(100);
  // State for the horizontal split (left/right)
  const [horizontalSplit, setHorizontalSplit] = useState(50);
  // State for the vertical split (top/bottom)
  const [verticalSplit, setVerticalSplit] = useState(50);

  useEffect(() => {
    const configs = {
      connectionBundle,
      apiUrl,
      roomId,
      sessionSid,
      examSid,
      installSid,
      identity,
      spToken,
      websocketUrl,
      enabledComponents,
      video,
      type,
      viewMode,
    };

    const initializeSession = async () => {
      // This will initialize the actual appliation...
      await AppStore.initialize(configs, props);
      // Register any callbacks in the event store...
      //EventStore.registerFromProps(props);
      EventStore.initialize(props);



    };
    // initialize the session first so we can validate it is even the right session and such...
    initializeSession();

    //}, [apiUrl, roomId, spToken, identity, websocketUrl]);
  }, []);


  useLayoutEffect(() => {
    if (sessionRef.current) {
      const elements = {
        session: sessionRef.current,
        video: videoContainerRef.current,
        controls: videoControlsRef.current,
        details: sessionDetailsRef.current,
      };

      LayoutStore.initialize(elements);

      //LayoutStore.adjustSizing();
    }

    const handleResize = debounce(() => {
      LayoutStore.adjustSizing();
    }, 300);

    //window.addEventListener('resize', handleResize);

    // Dispatch the event that the session has been loaded.
    if (initialized) {
      EventStore.trigger('onSessionLoaded', { roomId });
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [initialized]);
  // [mediaClient, room]

  useEffect(() => {
    if (!videoContainerSize || !controlsContainerSize) return;
    if (hasTracks) {
      setVideoHeight(
        videoContainerSize.height + (controlsContainerSize?.height || 40)
      );
    } else {
      setVideoHeight(350);
    }
  }, [videoContainerSize, controlsContainerSize]);

  useEffect(() => {
    // If the session is not live, go ahead and set the times...
    if (sessionRef.current) {
      LayoutStore.setSessionRef(sessionRef.current);
    }
  }, [sessionRef]);


  useHotkeys(
    'ctrl+1',
    () => {
      setVerticalSplit(10);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+2',
    () => {
      setVerticalSplit(20);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+3',
    () => {
      setVerticalSplit(30);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+4',
    () => {
      setVerticalSplit(40);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+5',
    () => {
      setVerticalSplit(50);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+6',
    () => {
      setVerticalSplit(60);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+7',
    () => {
      setVerticalSplit(70);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+8',
    () => {
      setVerticalSplit(80);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+9',
    () => {
      setVerticalSplit(90);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys('ctrl+0', () => LayoutStore.detailsSplitView.collapse());

  useHotkeys(
    'alt+1',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(10);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+2',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(20);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+3',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(30);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+4',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(40);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+5',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(50);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+6',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(60);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+7',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(70);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+8',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(80);
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'alt+9',
    () => {
      PlayerStore.setVideoPanelSplitPercentage(90);
    },
    { enableOnContentEditable: true }
  );

  useHotkeys(
    'ctrl+alt+1',
    () => {
      setVerticalSplit(10);
      PlayerStore.setVideoPanelSplitPercentage(10);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+2',
    () => {
      setVerticalSplit(20);
      PlayerStore.setVideoPanelSplitPercentage(20);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+3',
    () => {
      setVerticalSplit(30);
      PlayerStore.setVideoPanelSplitPercentage(30);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+4',
    () => {
      setVerticalSplit(40);
      PlayerStore.setVideoPanelSplitPercentage(40);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+5',
    () => {
      setVerticalSplit(50);
      PlayerStore.setVideoPanelSplitPercentage(50);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+6',
    () => {
      setVerticalSplit(60);
      PlayerStore.setVideoPanelSplitPercentage(60);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+7',
    () => {
      setVerticalSplit(70);
      PlayerStore.setVideoPanelSplitPercentage(70);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+8',
    () => {
      setVerticalSplit(80);
      PlayerStore.setVideoPanelSplitPercentage(80);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+9',
    () => {
      setVerticalSplit(90);
      PlayerStore.setVideoPanelSplitPercentage(90);
      LayoutStore.detailsSplitView.expand();
    },
    { enableOnContentEditable: true }
  );
  useHotkeys(
    'ctrl+alt+0',
    () => {
      setVerticalSplit(100);
      PlayerStore.setVideoPanelSplitPercentage(20);
      LayoutStore.detailsSplitView.collapse();
    },
    { enableOnContentEditable: true }
  );

  useHotkeys(
    'alt+f',
    () => {
      if (LayoutStore.isFullScreen) {
        LayoutStore.exitFullScreen();
      } else {
        LayoutStore.enterFullScreen();
      }
    },
    { enableOnContentEditable: true }
  );

  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.TOGGLE_DETAILS.KEY,
    () => {
      if (isCollapsed) {
        LayoutStore.detailsSplitView.expand();
      } else {
        LayoutStore.detailsSplitView.collapse();
      }
    },
    { enableOnContentEditable: true }
  );

  useHotkeys(
    CONSTANTS.KEYBOARD_SHORTCUTS.ANNOTATIONS.OPTIONS.FLOATING_MODAL.KEY,
    () => {
      annotationsModal.toggleModal();
    },
    { enableOnContentEditable: true }
  );

  return (
    <InstUISettingsProvider>
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <View
          as="div"
          height="100%"
          className="session-viewer-container"
          background="primary"
          elementRef={(el) => {
            sessionRef.current = el;
          }}>
          {(!initialized || !iam) ? (
            <SessionLoaderOverlay />
          ) : (
            <FullScreenProvider mountNode={sessionRef.current} isFullScreen={isFullScreen}>
              <IamValidationProvider
                iam={iam}
                withDebug={isDebugMode}
                onError={(error, context) => {
                  // TODO: Integrate with platform logging system
                  console.error('IAM Validation Error:', error.message, context);
                }}
              >
                <IamValidationGuard fallback={<SessionLoaderOverlay />}>
                  <View as="div" height="100%" background="primary">
                    <SplitView
                      direction="vertical"
                      tabIndex={2}
                      ref={(el) => {
                        // Store the SplitView reference in LayoutStore
                        if (el) {
                          LayoutStore.setDetailsSplitView(el);
                        }
                      }}
                      firstContent={
                        <>
                          <Header />
                          <View
                            height="calc(100% - 58px)"
                            background="primary"
                            as="div"
                            overflowX="hidden"
                            overflowY="hidden"
                            padding="xx-small none none none"
                            className="session-video-playback-container"
                          >
                            <VideoPlayer
                              {...props}
                              enabledComponents={AppStore.enabledComponents}
                            />
                          </View>
                        </>
                      }
                      secondContent={
                        <>
                          {/* Start: The session details... */}
                          {AppStore.enabledComponents.sessionDetails && (
                            <Flex.Item
                              as="div"
                              overflowX="hidden"
                              shouldShrink
                              shouldGrow
                              elementRef={(el) => {
                                sessionDetailsRef.current = el;
                              }}
                            >
                              <SessionDetails />
                            </Flex.Item>
                          )}

                          {/* END: The session details... */}
                        </>
                      }
                      initialSplit={50}
                      defaultSplit={50}
                      minFirstSize={10}
                      minSecondSize={20}
                      resizable={true}
                      collapsible={true}
                      collapseSize="43px"
                      collapsedPane="second"
                      splitPercentage={verticalSplit}
                      onChange={setVerticalSplit}
                    />
                  </View>
                  {/* Any modals or global items that need to be used throught the session... */}
                  <DenyConfirmation />
                </IamValidationGuard>
              </IamValidationProvider>
            </FullScreenProvider>
          )}
        </View>

        {/* Draggable Annotations Modal */}
        <DraggableAnnotationsModal
          isOpen={annotationsModal.isOpen}
          onClose={annotationsModal.closeModal}
          initialPosition={annotationsModal.position}
          initialSize={annotationsModal.size}
          mountNode={sessionRef.current}
        />
      </div>
    </InstUISettingsProvider>
  );
}
